import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Play, Stop, Coffee, ForkKnife, Wrench, MapPin, CheckCircle } from "@phosphor-icons/react";
import { getCoords } from "../lib/geo";
import { deviceId } from "../lib/device";
import { HeatBanner } from "../components/HeatBanner";
import BreakTimer from "../components/BreakTimer";
import { useI18n, bilingual } from "../lib/i18n";
import { uniqueRanches, repRanchId } from "../lib/ranchUtils";

const nowIso = () => new Date().toISOString();
const today = () => new Date().toISOString().slice(0, 10);

export default function MyTimecard() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const empId = user?.employee_id;

  const [jobs, setJobs] = useState([]);
  const [ranches, setRanches] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [entry, setEntry] = useState(null); // today's open/most-recent entry
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const [ranchId, setRanchId] = useState("");
  const [blockId, setBlockId] = useState("");
  const [category, setCategory] = useState("");
  const [jobId, setJobId] = useState("");
  const [operating, setOperating] = useState(""); // "", "no", "yes"
  const [equipIds, setEquipIds] = useState([]);
  const [policy, setPolicy] = useState({ rest_minutes: 10, meal_minutes: 30, reminder: "" });

  const load = async () => {
    const { data } = await api.get(`/time-entries?start_date=${today()}&end_date=${today()}`);
    const open = data.find((e) => !e.clock_out) || data[0] || null;
    setEntry(open);
  };
  useEffect(() => {
    (async () => {
      try {
        const [j, r, e, p] = await Promise.all([api.get("/jobcodes"), api.get("/ranches"), api.get("/equipment"), api.get("/break-policy")]);
        setJobs(j.data); setRanches(r.data); setEquipment(e.data.filter((x) => x.active !== false)); setPolicy(p.data);
      } catch { /* ignore */ }
      load();
    })();
  }, []); // eslint-disable-line

  const categories = useMemo(() => {
    const m = new Map();
    jobs.forEach((j) => { const n = j.category || "Uncategorized"; if (!m.has(n)) m.set(n, { code: j.category_code || "", es: j.category_es || "" }); });
    return [...m.entries()].map(([name, v]) => ({ name, code: v.code, es: v.es })).sort((a, b) => a.name.localeCompare(b.name));
  }, [jobs]);
  const categoryJobs = useMemo(() => (category ? jobs.filter((j) => (j.category || "Uncategorized") === category) : []), [jobs, category]);
  const ready = !!ranchId && !!category && !!jobId;

  const active = entry && !entry.clock_out ? entry : null;
  const openBreak = active?.breaks?.find((b) => !b.end);

  const post = async (payload) => {
    setBusy(true); setMsg("");
    try {
      const coords = payload.clock_in && !payload.clock_out ? await getCoords() : null;
      await api.post("/time-entries", {
        ...payload,
        employee_id: empId,
        latitude: coords?.lat ?? null,
        longitude: coords?.lng ?? null,
      });
      await load();
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const punchIn = async () => {
    if (!ready) { setMsg(t("mt.select_first")); return; }
    await post({
      client_id: crypto.randomUUID(),
      ranch_id: (blockId || ranchId), block: (ranches.find((r) => r.id === blockId)?.block) || null, job_code_id: jobId,
      work_date: today(), clock_in: nowIso(), breaks: [],
      equipment_ids: operating === "yes" ? equipIds : [],
      no_equipment_acknowledged: operating === "no",
    });
  };

  const base = () => ({
    client_id: active.client_id || active.id,
    ranch_id: active.ranch_id, job_code_id: active.job_code_id,
    work_date: active.work_date, clock_in: active.clock_in,
    breaks: active.breaks || [], equipment_ids: active.equipment_ids || [],
  });

  const punchOut = async () => {
    if (openBreak) { setMsg(t("mt.end_break_first")); return; }
    await post({ ...base(), clock_out: nowIso() });
  };
  const startBreak = async (kind) => {
    if (openBreak) { setMsg(t("mt.one_break")); return; }
    await post({ ...base(), breaks: [...(active.breaks || []), { kind, start: nowIso(), end: null }] });
  };
  const logBreakAudit = async (event, breakType, extra) => {
    try {
      const coords = await getCoords();
      await api.post("/break-audit", {
        crew_id: active?.crew_id || null,
        job_code: active?.job_code || null,
        break_type: breakType,
        event,
        latitude: coords?.lat ?? null,
        longitude: coords?.lng ?? null,
        device_id: deviceId(),
        device_info: navigator.userAgent,
        ...extra,
      });
    } catch { /* ignore */ }
  };
  const endBreak = async () => {
    const ob = openBreak;
    const end = nowIso();
    const breaks = (active.breaks || []).map((b) => (!b.end ? { ...b, end } : b));
    await post({ ...base(), breaks });
    if (ob) {
      const dur = Math.round(((new Date(end) - new Date(ob.start)) / 60000) * 100) / 100;
      logBreakAudit("completed", ob.kind, { start: ob.start, end, duration_minutes: dur });
    }
  };
  const cancelBreak = async () => {
    const ob = openBreak;
    const breaks = (active.breaks || []).filter((b) => b.end);
    await post({ ...base(), breaks });
    if (ob) logBreakAudit("accidental", ob.kind, { start: ob.start });
  };
  const toggleEquip = (id) => setEquipIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div className="space-y-6 max-w-3xl" data-testid="my-timecard-page">
      {active && openBreak && (
        <BreakTimer
          kind={openBreak.kind}
          start={openBreak.start}
          durationMinutes={openBreak.kind === "meal" ? policy.meal_minutes : policy.rest_minutes}
          reminder={policy.reminder}
          busy={busy}
          onEnd={endBreak}
          onAccidental={cancelBreak}
        />
      )}
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{t("mt.title")}</h1>
        <p className="text-sm font-medium tracking-wide uppercase text-stone-500">{user?.name} · {t("mt.role")}</p>
      </div>

      <HeatBanner />
      {msg && <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-2 text-sm font-medium" data-testid="mt-message">{msg}</div>}

      {active ? (
        <div className="bg-[#1F3D34] text-white p-6 shadow-sm" data-testid="mt-active-card">
          <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide">
            <span className="h-2.5 w-2.5 rounded-full bg-[#7C9673] animate-pulse" /> {openBreak ? `${t("mt.on_break")} (${openBreak.kind === "meal" ? t("bt.meal") : t("bt.rest")})` : t("mt.on_clock")}
          </div>
          <div className="text-3xl font-bold mt-2 font-mono">{t("tc.in")} {new Date(active.clock_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
          <div className="text-xs font-mono mt-1 opacity-80">{active.job_code || "—"}</div>
          {active.ranch_name && <div className="text-xs font-mono mt-0.5 opacity-80 flex items-center gap-1"><MapPin weight="fill" size={12} /> {active.ranch_name}</div>}
          {active.equipment_ids?.length > 0 && (
            <div className="text-xs font-mono mt-0.5 opacity-80 flex items-center gap-1"><Wrench weight="fill" size={12} /> {active.equipment_ids.length} {t("mt.equipment")}</div>
          )}
          <div className="grid grid-cols-2 gap-3 mt-5">
            {!openBreak ? (
              <>
                <button data-testid="mt-meal-start" disabled={busy} onClick={() => startBreak("meal")} className="h-14 bg-white/10 border border-white/20 font-bold uppercase text-sm flex items-center justify-center gap-2"><ForkKnife weight="bold" /> {t("bt.meal")}</button>
                <button data-testid="mt-rest-start" disabled={busy} onClick={() => startBreak("rest")} className="h-14 bg-white/10 border border-white/20 font-bold uppercase text-sm flex items-center justify-center gap-2"><Coffee weight="bold" /> {t("bt.rest")}</button>
              </>
            ) : (
              <button data-testid="mt-break-end" disabled={busy} onClick={endBreak} className="col-span-2 h-14 bg-[#D8C7A1] text-[#1F3D34] font-bold uppercase text-sm flex items-center justify-center gap-2"><CheckCircle weight="bold" /> {t("mt.end_break")}</button>
            )}
            <button data-testid="mt-punch-out" disabled={busy} onClick={punchOut} className="col-span-2 h-16 bg-[#7C9673] font-bold uppercase text-base flex items-center justify-center gap-2 disabled:opacity-50"><Stop weight="fill" size={22} /> {t("mt.clock_out")}</button>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 p-6 shadow-sm space-y-4" data-testid="mt-punchin-card">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select data-testid="mt-ranch" value={repRanchId(ranches, ranchId)} onChange={(e) => { setRanchId(e.target.value); setBlockId(""); }} className="h-12 px-3 border border-stone-200 font-medium">
              <option value="">{t("mt.ranch")} *</option>
              {uniqueRanches(ranches).map((r) => <option key={r.id} value={r.id}>{r.ranch_number} · {r.name}</option>)}
            </select>
            {ranchId && (() => {
              const sel = ranches.find((r) => r.id === repRanchId(ranches, ranchId));
              const blocks = sel ? ranches.filter((r) => r.ranch_number === sel.ranch_number && r.block) : [];
              if (blocks.length === 0) return null;
              return (
                <select data-testid="mt-block" value={blockId} onChange={(e) => setBlockId(e.target.value)} className="h-12 px-3 border border-stone-200 font-medium">
                  <option value="">Whole ranch — no block</option>
                  {blocks.map((b) => <option key={b.id} value={b.id}>{b.block}{b.variety ? ` · ${b.variety}` : ""}</option>)}
                </select>
              );
            })()}
            <select data-testid="mt-category" value={category} onChange={(e) => { setCategory(e.target.value); setJobId(""); }} className="h-12 px-3 border border-stone-200 font-medium">
              <option value="">{t("mt.category")} *</option>
              {categories.map((c) => <option key={c.name} value={c.name}>{c.code ? `${c.code} · ` : ""}{bilingual(c.name, c.es, lang)}</option>)}
            </select>
            <select data-testid="mt-job" value={jobId} disabled={!category} onChange={(e) => setJobId(e.target.value)} className="h-12 px-3 border border-stone-200 font-medium disabled:bg-stone-100">
              <option value="">{category ? `${t("mt.job")} *` : t("mt.pick_category")}</option>
              {categoryJobs.map((j) => <option key={j.id} value={j.id}>{j.job_code || j.code} · {bilingual(j.job || j.description, j.job_es, lang)}</option>)}
            </select>
          </div>

          {jobId && (
            <div className="border border-stone-200 p-4">
              <div className="font-semibold text-sm mb-2">{t("tc.operating_q")}</div>
              <div className="flex gap-2">
                <button data-testid="mt-equip-no" onClick={() => { setOperating("no"); setEquipIds([]); }} className={`h-11 px-4 border font-bold uppercase text-xs ${operating === "no" ? "bg-[#1F3D34] text-white border-[#1F3D34]" : "border-stone-300"}`}>{t("tc.no_equipment")}</button>
                <button data-testid="mt-equip-yes" onClick={() => setOperating("yes")} className={`h-11 px-4 border font-bold uppercase text-xs flex items-center gap-1 ${operating === "yes" ? "bg-[#7C9673] text-white border-[#7C9673]" : "border-stone-300"}`}><Wrench weight="bold" /> {t("tc.yes_choose")}</button>
              </div>
              {operating === "yes" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 max-h-64 overflow-y-auto" data-testid="mt-equip-list">
                  {equipment.map((eq) => (
                    <button key={eq.id} data-testid={`mt-equip-${eq.id}`} onClick={() => toggleEquip(eq.id)}
                      className={`text-left border p-2 ${equipIds.includes(eq.id) ? "bg-[#7C9673]/20 border-[#7C9673]" : "border-stone-200"}`}>
                      <div className="font-mono text-xs font-bold">{eq.code} · {eq.name}</div>
                      <div className="text-[10px] uppercase text-stone-500">{eq.category || "—"}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <button data-testid="mt-punch-in" disabled={busy || !ready} onClick={punchIn}
            className="w-full h-16 bg-[#7C9673] text-white font-bold uppercase text-base flex items-center justify-center gap-2 border border-stone-200 disabled:opacity-40 disabled:cursor-not-allowed">
            <Play weight="fill" size={22} /> {t("mt.clock_in")}
          </button>
        </div>
      )}
    </div>
  );
}
